import React, { useEffect, useRef, useState } from 'react'

import { GoToLocation, IsEmpty, IsNotEmpty, ValidateEmail } from '../Helpers';
import { useErrorDisplay } from '../components/ErrorDisplay';
import AccountClient from '../apiClients/AccountClient';
import { InvitationItem } from '../apiClients/ManageOrganizationClient';
import { InvitationState, OrgAssociationType, SessionStorageItemIds } from '../Enums';
import { Box, Button, DialogActions, Divider, Grid, Paper, TextField, useTheme } from '@mui/material';
import BusySpinner from '../components/BusySpinner';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { loginRequest, signInAcceptInvitationRequest } from '../AuthConfig';
import useViewportMode from '../hooks/useViewportMode';

export interface AcceptInvitationPageProps {

}

const AcceptInvitationPage = ({ }: AcceptInvitationPageProps) => {

    const theme = useTheme();
    const { mode: viewportMode, isPhone, isDesktop } = useViewportMode();
    const initialized = useRef<string>();
    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);

    const [showValidation, setShowValidation] = useState<boolean>(false);
    const [firstName, setFirstName] = useState<string>();
    const [lastName, setLastName] = useState<string>();
    const [email, setEmail] = useState<string>();
    const [password, setPassword] = useState<string>();
    const [password2, setPassword2] = useState<string>();
    const [invitation, setInvitation] = useState<InvitationItem>();

    const { instance } = useMsal();
    const accessTokenRequest = {
        authority: signInAcceptInvitationRequest.authority,
        scopes: loginRequest.scopes,
        // account: accounts[0],
    };

    const loadData = async () => {
        try {
            setBusy(true);

            let inviteId = sessionStorage.getItem(SessionStorageItemIds.InvitationId);

            if (IsEmpty(inviteId)) {
                const query = new URLSearchParams(window.location.search);
                inviteId = query.get("id") ?? query.get("Id") ?? query.get("ID");

                // put the invitationId in session storage for after we login
                sessionStorage.setItem(SessionStorageItemIds.InvitationId, inviteId);
            }

            if (IsNotEmpty(inviteId)) {
                var response = await new AccountClient().GetInvitation(inviteId);
                setInvitation(response);
                setEmail(response.EmailAddress);
            }

        } catch (error) {
            setError(error, "Failed to find the invitation");
        } finally {
            setBusy(false);
        }
    }

    useEffect(() => {

        if (initialized.current === 'true')
            return;

        initialized.current = 'true';
        loadData();

    }, []);

    const form_validated = () => {
        return !(IsEmpty(firstName) || IsEmpty(lastName) || IsEmpty(password) || IsEmpty(password2) || IsEmpty(email) || password !== password2)
    }

    const createAccount = async () => {
        try {
            if (!form_validated()) {
                setShowValidation(true);
                return;
            }
            setBusy(true);

            let roleTemplate = invitation.RoleTemplate;
            if (invitation.AssociationType === OrgAssociationType.Consultant)
                roleTemplate = "Consultant";

            var response = await new AccountClient().CreateAccount(invitation.Id, { FirstName: firstName, LastName: lastName, Email: email, Password: password, RoleTemplate: roleTemplate });
            if (response.IsError) {
                setError(response.Errors[0].replace("Error: ", ""), "There was an error creating your account");
            }
            else {
                //redirect the user to the login page
                gotoLogin();
            }
        } catch (error) {
            setError(error, "Failed to create the account");
        } finally {
            setBusy(false);
        }
    }

    const acceptInvitation = async () => {
        try {
            setBusy(true);
            await new AccountClient().AcceptInvitation(invitation.Id);
            sessionStorage.removeItem(SessionStorageItemIds.InvitationId);
            GoToLocation("");
        } catch (error) {
            setError(error, "Failed to accept the invitation");
        } finally {
            setBusy(false);
        }
    }

    const rejectInvitation = async () => {
        try {
            setBusy(true);
            await new AccountClient().RejectInvitation(invitation.Id);
            sessionStorage.removeItem(SessionStorageItemIds.InvitationId);
            GoToLocation("");
        } catch (error) {
            setError(error, "Failed to reject the invitation");
        } finally {
            setBusy(false);
        }
    }

    const gotoLogin = async () => {
        // login using the signInAcceptInvitation authority
        // on success Azure will redirect back to the app
        // and we will evaluate the authority in the login
        // callback in the useEffect in app.tsx
        // which will redirect here with the alive param set
        // to true
        try {
            // store organization id in session
            sessionStorage.setItem(SessionStorageItemIds.LastOrganizationId, invitation.OrganizationId);
            // login
            instance.acquireTokenRedirect(accessTokenRequest);
        } catch (error) {
            setError(error, "There was an issue logging you in.");
        }
    }

    if (IsEmpty(invitation)) {
        return null;
    }

    if (invitation.State !== InvitationState.Sent) {
        GoToLocation("");
    }

    const pageSx = !isDesktop ? {
        minHeight: '100vh',
        '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
        overflowY: 'auto',
        px: isPhone ? 1.5 : 3,
        pt: isPhone ? 1.5 : 3,
        pb: 'calc(16px + env(safe-area-inset-bottom, 0px))',
    } : undefined;
    const cardSx = {
        maxWidth: 600,
        borderRadius: '5px',
        mx: !isDesktop ? 'auto' : undefined,
        ...(!isDesktop && {
            width: '100%',
            '& .MuiInputBase-root, & .MuiButtonBase-root': { minHeight: 44 },
        }),
    };
    const actionSx = !isDesktop ? {
        px: 2,
        py: 1.5,
        pb: 'calc(12px + env(safe-area-inset-bottom, 0px))',
        flexDirection: isPhone ? 'column' : 'row',
        gap: 1,
        '& > :not(:first-of-type)': { marginLeft: 0 },
        '& .MuiButton-root': { minHeight: 44, width: isPhone ? '100%' : 'auto' },
    } : undefined;

    return (
        <>
            <AuthenticatedTemplate>
                <ErrorDisplay />
                <Box data-viewport-mode={viewportMode} sx={pageSx}>
                <Grid container spacing={2} justifyContent={!isDesktop ? 'center' : undefined}>
                    {isDesktop && <Grid item xs={12} sm={4}></Grid>}
                    <Grid item xs={12} sm={isDesktop ? 4 : 10} md={isDesktop ? undefined : 8}>
                        <Paper sx={cardSx}>
                            <BusySpinner busy={busy}>
                                <Box component='div' sx={{
                                    borderRadius: '5px 5px 0px 0px',
                                    width: '100%',
                                    padding: '0px 15px 15px 15px',
                                    backgroundColor: theme.palette.primary.main
                                }}>
                                    <h1 style={{ margin: '0px' }}>Accept Invitation</h1>
                                </Box>
                                <Divider />
                                <Box sx={{ m: isDesktop ? '15px' : 0, p: isDesktop ? 0 : 2 }}>
                                    <p>Invitation for: <span style={{ color: 'orange' }}>{invitation?.EmailAddress}</span></p>
                                    <p>You have been invited to join {invitation?.OrganizationName} as a {invitation?.AssociationType}.</p>
                                    <p>Do you accept?</p>
                                </Box>
                                <Divider />
                                <DialogActions sx={actionSx}>
                                    <Button disabled={busy} variant="contained" onClick={acceptInvitation}>Accept</Button>
                                    <Button disabled={busy} onClick={rejectInvitation} color="primary">Reject</Button>
                                </DialogActions>
                            </BusySpinner>
                        </Paper>
                    </Grid>
                    {isDesktop && <Grid item xs={12} sm={4}></Grid>}
                </Grid>
                </Box>
            </AuthenticatedTemplate>
            <UnauthenticatedTemplate>
                <ErrorDisplay />
                <Box data-viewport-mode={viewportMode} sx={pageSx}>
                <Grid container spacing={2} justifyContent={!isDesktop ? 'center' : undefined}>
                    {isDesktop && <Grid item xs={12} sm={4}></Grid>}
                    <Grid item xs={12} sm={isDesktop ? 4 : 10} md={isDesktop ? undefined : 8}>
                        <Paper sx={cardSx}>
                            <BusySpinner busy={busy}>
                                <Box component='div' sx={{
                                    borderRadius: '5px 5px 0px 0px',
                                    width: '100%',
                                    padding: '0px 15px 15px 15px',
                                    backgroundColor: theme.palette.primary.main
                                }}>
                                    <h1 style={{ margin: '0px' }}>Create Account</h1>
                                    <h5 style={{ margin: '0px' }}>Email and password</h5>
                                </Box>
                                <Divider />
                                <Box sx={{ m: isDesktop ? '15px' : 0, p: isDesktop ? 0 : 2 }}>
                                    <TextField label="First Name"
                                        fullWidth
                                        variant="outlined"
                                        value={firstName}
                                        error={showValidation && IsEmpty(firstName)}
                                        onChange={(e) => setFirstName(e.target.value)}
                                        disabled={busy} />
                                    <div style={{ height: '15px' }} />
                                    <TextField label="Last Name"
                                        fullWidth
                                        variant="outlined"
                                        value={lastName}
                                        error={showValidation && IsEmpty(lastName)}
                                        onChange={(e) => setLastName(e.target.value)}
                                        disabled={busy} />
                                    <div style={{ height: '15px' }} />
                                    <TextField label="Email"
                                        fullWidth
                                        variant="outlined"
                                        value={email}
                                        error={showValidation && !ValidateEmail(email)}
                                        onChange={(e) => setEmail(e.target.value)}
                                        disabled={busy} />
                                    <div style={{ height: '15px' }} />
                                    <TextField label="Password"
                                        fullWidth
                                        variant="outlined"
                                        type="password"
                                        error={showValidation && IsEmpty(password)}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        disabled={busy} />
                                    <div style={{ height: '15px' }} />
                                    <TextField label="Validate Password"
                                        fullWidth
                                        variant="outlined"
                                        type="password"
                                        error={showValidation && (IsEmpty(password2) || password2 !== password)} value={password2}
                                        onChange={(e) => setPassword2(e.target.value)}
                                        disabled={busy} />
                                </Box>
                                <Divider />
                                <DialogActions sx={actionSx}>
                                    <Button disabled={busy} onClick={gotoLogin} color='primary'>I already have an account, Goto login!</Button>
                                    <Button disabled={busy} onClick={createAccount} variant="contained">Create Account</Button>
                                </DialogActions>
                            </BusySpinner>
                        </Paper>
                    </Grid>
                    {isDesktop && <Grid item xs={12} sm={4}></Grid>}
                </Grid>
                </Box>
            </UnauthenticatedTemplate>
        </>
    )
};

export default AcceptInvitationPage;
